import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { URLS_TO_IGNORE_REFRESH_TOKEN } from '../../config/interceptor.ts';
import { getMonitoring } from '../../monitoring/monitoring.ts';
import '../http-client.types.ts';
import { handleUnauthorized } from '../interceptors/unauthorized.handler.ts';
import { is422Status, is4xxStatus, is5xxStatus } from '../utils/status.functions.ts';
import { isRetryableRequest } from './retryable-endpoints.ts';

const DEFAULT_RETRY_ATTEMPTS = 5;

export const buildURL = (baseURL?: string, url?: string) => {
  if (!baseURL) return url;
  if (!url) return baseURL;
  if (/^https?:\/\//.exec(url)) return url;

  let cleanBaseURL = baseURL;
  while (cleanBaseURL.endsWith('/')) {
    cleanBaseURL = cleanBaseURL.slice(0, -1);
  }

  let cleanURL = url;
  while (cleanURL.startsWith('/')) {
    cleanURL = cleanURL.slice(1);
  }

  return `${cleanBaseURL}/${cleanURL}`;
};

function makeDirectHttpRequest(config: AxiosRequestConfig): Promise<AxiosResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(config.method?.toUpperCase() ?? 'GET', buildURL(config.baseURL, config.url) ?? '');

    if (config.headers) {
      Object.entries(config.headers).forEach(([key, value]) => {
        if (typeof value === 'string') {
          xhr.setRequestHeader(key, value);
        }
      });
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data: unknown = xhr.response ? JSON.parse(xhr.response as string) : undefined;
          resolve({ data, status: xhr.status, statusText: xhr.statusText, headers: {}, config } as AxiosResponse);
        } catch {
          resolve({
            data: xhr.response as unknown,
            status: xhr.status,
            statusText: xhr.statusText,
            headers: {},
            config
          } as AxiosResponse);
        }
      } else {
        const errorObj = new Error(`HTTP Error ${xhr.status}`) as AxiosError;
        errorObj.response = {
          status: xhr.status,
          data: xhr.response as unknown,
          statusText: xhr.statusText,
          headers: {},
          config
        } as AxiosResponse;
        errorObj.status = xhr.status;
        reject(errorObj);
      }
    };

    xhr.onerror = () => {
      const errorObj = new Error('Network Error') as AxiosError;
      errorObj.response = {
        status: xhr.status,
        data: xhr.response as unknown,
        statusText: xhr.statusText,
        headers: {},
        config
      } as AxiosResponse;
      errorObj.status = xhr.status;
      reject(errorObj);
    };

    if (config.data) {
      xhr.send(typeof config.data === 'string' ? config.data : JSON.stringify(config.data));
    } else {
      xhr.send();
    }
  });
}

async function retryWithGenericStrategy(error: AxiosError): Promise<AxiosResponse> {
  const errorConfig = error.config;
  if (!errorConfig) throw error;

  const suggestedTiming = [2, 5, 10, 20, 30];
  const maxRetryAttempts = suggestedTiming.length;

  for (let attempt = 0; attempt < maxRetryAttempts; attempt++) {
    try {
      return await makeDirectHttpRequest(errorConfig);
    } catch (requestError) {
      const axiosError = requestError as AxiosError;
      const status = axiosError.response?.status ?? axiosError.status;

      if (status && status < 500) throw requestError;
      if (attempt === maxRetryAttempts - 1) throw requestError;

      const delayTime = suggestedTiming[attempt];
      await new Promise(resolve => setTimeout(resolve, delayTime * 1000));
    }
  }

  throw error;
}

export async function handleResponseError(error: AxiosError) {
  const url = error.config?.url ?? '';
  const status = error?.response?.status;
  const statusText = status === undefined ? '' : String(status);
  const monitoring = getMonitoring();
  const domain = error.config?.domain ?? 'AxiosInterceptor';

  const shouldSkipRefresh = URLS_TO_IGNORE_REFRESH_TOKEN.some(u => url.includes(u));

  if (!shouldSkipRefresh && status === 401) {
    return handleUnauthorized(error);
  }

  if (is5xxStatus(statusText)) {
    const method = error.config ? (error.config.method ?? 'GET').toUpperCase() : undefined;
    const willRetry = isRetryableRequest(method, error.config?.url);

    monitoring.captureError(error, {
      url: error.config?.url,
      method,
      status,
      data: error?.response?.data as Record<string, string> | string,
      errorType: 'AxiosError',
      domain,
      retried: willRetry
    });

    if (!willRetry) {
      throw error;
    }

    try {
      return await retryWithGenericStrategy(error);
    } catch (retryError) {
      monitoring.captureError(retryError as AxiosError, {
        url: error.config?.url,
        method,
        status: (retryError as AxiosError)?.response?.status,
        data: (retryError as AxiosError)?.response?.data as Record<string, string> | string,
        errorType: 'AxiosError',
        domain,
        retried: true,
        retryAttempts: DEFAULT_RETRY_ATTEMPTS
      });
      throw retryError;
    }
  }

  if (is422Status(statusText)) {
    monitoring.captureError(error, { domain: domain === 'AxiosInterceptor' ? 'Architecture' : domain });
  } else if (is4xxStatus(statusText)) {
    monitoring.captureError(error, {
      url: error.config?.url,
      method: error.config?.method,
      status,
      data: error?.response?.data as Record<string, string> | string,
      errorType: 'AxiosError',
      domain
    });
  }

  throw error;
}
